/**
 * Palantir PMS - Software para la gestión de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creación / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso está restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

import { useEffect, useLayoutEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "./api/axiosInstance";
import { installCrossTabFetchBroadcaster } from "./utils/crossTabDataSync";



import { Routes, Route, Navigate } from "react-router-dom";
import Navbar from "./Components/Navbar"; 

import NewReservation from "./Pages/NewReservation";
import ReservationAccountView from "./reservations/ReservationAccountView";
import ReservationGroupView from "./reservations/ReservationGroupView";
import SelectCustomer from "./Pages/SelectCustomer";
import NewCustomer from "./Pages/NewCustomer";
import ReservationDetails from "./Pages/ReservationDetails";
import CheckIn from "./Pages/CheckIn";
import FrontDesk from "./Pages/FrontDesk"; 
import RoomsManagement from "./Pages/RoomsManagement";
import EditReservation from "./Pages/EditReservation";
import ReservationsTabs from "./Pages/ReservationsTabs";
import RevenueManagement from "./Pages/RevenueManagement";
import EditRatePrice from "./Pages/EditRatePrice";
import NewStopSell from "./Pages/NewStopSell";
import RoomRateForm from "./Pages/RoomRateForm";
import Settings from "./Pages/Settings";
import LegalEntitySettings from "./Pages/LegalEntitySettings";
import GlobalUsersRoles from "./Pages/GlobalUsersRoles";
import LoginScreen from "./Pages/LoginScreen";
import WorkspaceSelectionScreen from "./Pages/WorkspaceSelectionScreen";
import SessionAccessGate from "./Components/SessionAccessGate";
import PrivateRoute from "./Components/PrivateRoute";
import useDraftCleanupOnNavigation from "./hooks/useDraftCleanupOnNavigation";
import AdminPanel from "./Pages/admin/AdminPanel";
import CorporatePurchasingApp, { CorporatePurchasingLegacyRedirect } from "./corporate-purchasing/CorporatePurchasingApp";
import BusinessUnitsList from "./Pages/admin/BusinessUnitsList";
import CurrenciesAdmin from "./Pages/admin/CurrenciesAdmin";
import RoomCategoriesList from "./Pages/admin/RoomCategoriesList";
import RoomsList from "./Pages/admin/RoomsList";
import MarketIntelligence from "./Pages/admin/MarketIntelligence";
import AcceptInvitationScreen from "./Pages/AcceptInvitationScreen";
import ResetPasswordScreen from "./Pages/ResetPasswordScreen"; // O el path correcto
import ViewDB from "./Pages/admin/ViewDB";
import AdminCron from "./Pages/admin/AdminCron";
import ReservationGeneratorAdmin from "./Pages/admin/ReservationGeneratorAdmin";
import ReviewSourcesAdmin from "./Pages/admin/ReviewSourcesAdmin";
import ReviewTopicsCatalogAdmin from "./Pages/admin/ReviewTopicsCatalogAdmin";
import Integrations from "./Pages/admin/Integrations";
import ChannelManagers from "./Pages/admin/ChannelManagers";
import PaymentGateways from "./Pages/admin/PaymentGateways";
import CrmProviderIntegrations from "./Pages/admin/CrmProviderIntegrations";
import BusinessUnitProviders from "./Pages/admin/BusinessUnitProviders";
import PosPurchasingIntegrations from "./Pages/admin/PosPurchasingIntegrations";
import WhatsAppDeliveryNoteIntegrations from "./Pages/admin/WhatsAppDeliveryNoteIntegrations";
import WhatsAppCrmIntegrations from "./Pages/admin/WhatsAppCrmIntegrations";
import UserManagement from "./Pages/admin/UserManagement";
import AdminOccupancyAudit from "./Pages/admin/AdminOccupancyAudit";
import AdminLogs from "./Pages/admin/AdminLogs";
import AdminSecurityLogs from "./Pages/admin/AdminSecurityLogs";
import OnboardingSetupAdmin from "./Pages/admin/OnboardingSetupAdmin";
import OnboardingAdminHub from "./Pages/admin/OnboardingAdminHub";
import InventoryLocationReconciliationAdmin from "./Pages/admin/InventoryLocationReconciliationAdmin";
import InventoryLocationIncidentsAdmin from "./Pages/admin/InventoryLocationIncidentsAdmin";
import ModuleSelector from "./Components/ModuleSelector";
import PurchasingMeasurementUnits from "./Pages/admin/PurchasingMeasurementUnits";
import PurchasingApp from "./purchasing/PurchasingApp";
import GuestOrderPage from "./pos/public/GuestOrderPage";
import ServiceDisplayPage from "./pos/public/ServiceDisplayPage";
import CustomerCreditPage from "./pos/public/CustomerCreditPage";
import PosApp from "./pos/PosApp";
import AnalyticsApp from "./analytics/AnalyticsApp";
import OnboardingApp from "./onboarding/OnboardingApp";
import CrmApp from "./crm/CrmApp";
import CrmTaskReminderPopup from "./crm/components/CrmTaskReminderPopup";
import OnboardingRoomCategoriesAdmin from "./onboarding/admin/OnboardingRoomCategoriesAdmin";
import OnboardingRoomsAdmin from "./onboarding/admin/OnboardingRoomsAdmin";
import OnboardingRoomRatesAdmin from "./onboarding/admin/OnboardingRoomRatesAdmin";
import OnboardingReservationSettingsAdmin from "./onboarding/admin/OnboardingReservationSettingsAdmin";
import OnboardingExtrasDiscountsAdmin from "./onboarding/admin/OnboardingExtrasDiscountsAdmin";
import OnboardingBookingEngineAdmin from "./onboarding/admin/OnboardingBookingEngineAdmin";
import RevenueManagementContainer from "./Pages/RevenueManagementContainer";
import RevenueOptimizerView from "./Pages/RevenueOptimizerView";
import { canViewRms } from "./rms/rmsAccessControl";
import PmsPanel from "./Pages/PmsPanel";
import PmsDashboard from "./Pages/PmsDashboard";
import ReportsStayPerformance from "./Pages/ReportsStayPerformance";
import MarketIntelligenceView from "./Pages/MarketIntelligenceView";
import ReviewImportPage from "./reviews-intelligence/ReviewImportPage";
import AllReviewsPage from "./reviews-intelligence/AllReviewsPage";
import ReviewOperationsPage from "./reviews-intelligence/ReviewOperationsPage";
import TrendsActionsPage from "./reviews-intelligence/TrendsActionsPage";
import ReviewsDashboardPage from "./reviews-intelligence/ReviewsDashboardPage";
import GuestFeedbackSurveysPage from "./reviews-intelligence/GuestFeedbackSurveysPage";
import GuestFeedbackSurveyPublicPage from "./reviews-intelligence/GuestFeedbackSurveyPublicPage";
import DemoEntry from "./demo/DemoEntry";
import DemoDesktopOnlyGuard from "./demo/DemoDesktopOnlyGuard";
import DemoModeBanner from "./demo/DemoModeBanner";
import { DEMO_BUSINESS_UNIT_ID, isDemoMode } from "./demo/demoMode";
import { getDemoReadOnlyRedirectPath } from "./demo/demoAccessPolicy";
import { resolveFirstPmsEntryPath } from "./utils/moduleEntryResolver";
import {
  persistAuthSession,
  readStoredAuthToken,
  readStoredUser,
} from "./utils/sessionUser";
import { createSessionRefreshController } from "./utils/sessionRefreshController";

import BookingApp from "./booking/BookingApp";
import BookingCancel from "./booking/BookingCancel";
import CommercialSite from "./comercial/CommercialSite";







import 'react-datepicker/dist/react-datepicker.css';




import 'bootstrap/dist/css/bootstrap.min.css'; // ✅ Importar Bootstrap
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import './index.css';




const PmsPermissionRoute = ({ allowed, children, message }) => {
  if (!allowed) {
    return (
      <div
        style={{
          color: "inherit",
          textAlign: "center",
          marginTop: "4rem",
          fontSize: "1.2rem",
        }}
      >
        {message || "You don't have permission to access this section."}
      </div>
    );
  }

  return children;
};

const NavigatePreservingSearch = ({ to }) => {
  const location = useLocation();
  return <Navigate to={`${to}${location.search || ""}`} replace />;
};

const NavigateToDemoReadOnlyPath = ({ to }) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set("business_unit_id", String(DEMO_BUSINESS_UNIT_ID));
  return <Navigate to={`${to}?${params.toString()}`} replace />;
};

const NavigateToRoomNumbersGrid = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set("view", "grid");
  params.set("grid_view", "room_numbers");
  return <Navigate to={`/reservations/list?${params.toString()}`} replace />;
};

const sessionAccessFingerprint = (user = {}) =>
  JSON.stringify({
    role: user?.role || null,
    permissions: user?.permissions || {},
    allowed_modules: Array.isArray(user?.allowed_modules) ? user.allowed_modules : [],
    business_unit_ids: Array.isArray(user?.business_unit_ids) ? user.business_unit_ids : [],
    active_business_unit_id: user?.active_business_unit_id || null,
    has_access_to_all_business_units: Boolean(user?.has_access_to_all_business_units),
    corporate_modules: user?.corporate_modules,
    has_business_unit_access: user?.has_business_unit_access,
    local_business_unit_ids: user?.local_business_unit_ids,
  });

function App() {

  const [businessUnits, setBusinessUnits] = useState([]);
  const [selectedBusinessUnit, setSelectedBusinessUnit] = useState("");

  const location = useLocation();
  const currentUser = readStoredUser();
  const currentPermissions = currentUser?.permissions || {};
  const currentAllowedModules = Array.isArray(currentUser?.allowed_modules)
    ? currentUser.allowed_modules.map((moduleKey) => String(moduleKey).trim().toLowerCase())
    : [];
  const canAccessCrmModule = currentAllowedModules.includes("crm");
  const isCurrentUserAdmin = Number(currentUser?.id) === 1;
  const canPmsPermission = (key) => isCurrentUserAdmin || Boolean(currentPermissions?.[key]);
  const canAccessRevenueOptimizer =
    isCurrentUserAdmin || canViewRms(currentPermissions);
  const canAccessReviewIntelligence = canPmsPermission("reviews_intelligence.access");
  const canAccessReports = canPmsPermission("reports.view");
  const canViewLegalEntitySettings =
    canPmsPermission("access_settings") ||
    canPmsPermission("finance.settings.view") ||
    canPmsPermission("finance.settings.manage_legal_entity") ||
    canPmsPermission("finance.settings.manage_taxes");
  const pmsEntryPath = resolveFirstPmsEntryPath(currentPermissions, {
    isAdmin: isCurrentUserAdmin,
  });

  const isPublicBooking = location.pathname.startsWith("/booking");
  const isCommercialRoute = location.pathname.startsWith("/comercial");
  const isGuestFeedbackSurveyRoute = location.pathname.startsWith("/guest-feedback-survey/");
  const normalizedPathname = location.pathname.replace(/\/+$/, "") || "/";
  const isAuthRoute =
    normalizedPathname === "/" ||
    normalizedPathname === "/login" ||
    normalizedPathname === "/demo" ||
    normalizedPathname === "/select-module" ||
    normalizedPathname === "/select-workspace" ||
    (normalizedPathname.startsWith("/reset-password") || normalizedPathname.startsWith("/accept-invitation"));
  const isOnboardingRoute = location.pathname.startsWith("/onboarding");
  const isCrmRoute = location.pathname.startsWith("/crm");
  const isPosRoute = location.pathname.startsWith("/pos");
  const isGuestOrderRoute =
    normalizedPathname === "/pos/guest-order" ||
    location.pathname.startsWith("/pos/guest-order/");
  const isServiceDisplayRoute =
    normalizedPathname === "/pos/service-display" ||
    location.pathname.startsWith("/pos/service-display/");
  const isTableOrderRoute = location.pathname.startsWith("/pos/table-order/");
  const isCustomerCreditPublicRoute = location.pathname.startsWith("/pos/customer-credit/");
  const isPublicPosDeviceRoute = isGuestOrderRoute || isServiceDisplayRoute || isTableOrderRoute || isCustomerCreditPublicRoute;
  const isAnalyticsRoute = location.pathname.startsWith("/analytics");
  const isCorporatePurchasingRoute = /^\/corporate-purchasing(?:\/|$)/.test(location.pathname);
  const isPmsPanelRoute =
    location.pathname.startsWith("/reservations") ||
    location.pathname.startsWith("/reservation-group") ||
    location.pathname.startsWith("/front-desk") ||
    location.pathname.startsWith("/rooms-management") ||
    location.pathname.startsWith("/settings") ||
    location.pathname.startsWith("/reports") ||
    location.pathname.startsWith("/revenue-management-container") ||
    location.pathname.startsWith("/reviews-intelligence") ||
    location.pathname.startsWith("/edit-room-rate");
  const isPilcorePurchasingRoute = /^\/purchasing\/(?:dashboard|suppliers|recipes|settings\/services|items(?:\/(?:items|families|subfamilies|categories|subcategories))?|reports(?:\/(?:items-purchased|item-last-purchase|item-last-unit-cost))?|service-orders(?:\/approvals)?|orders(?:\/(?:requisitions|purchase-orders|approvals|receivings|supplier-returns|delivery-notes))?|stock-management\/(?:inventory-intelligence|current-stock|snapshot-report|inventory-count|requisitions(?:\/approvals)?|transfers(?:\/approvals)?|returns|dispatched))$/.test(normalizedPathname);
  const usesPilcoreWorkspaceShell = isPmsPanelRoute || isPilcorePurchasingRoute || isCorporatePurchasingRoute;
  const navigate = useNavigate(); 
  const demoModeActive = isDemoMode();

  useEffect(() => {
    installCrossTabFetchBroadcaster();
  }, []);
  
  useLayoutEffect(() => {
    const navbar = document.querySelector(".navbar");

    if (isAuthRoute) {
      if (navbar) {
        navbar.style.setProperty("display", "none", "important");
        navbar.style.setProperty("height", "0px", "important");
      }

      document.body.classList.add("login-screen");
      document.body.style.paddingTop = "0";
      return;
    }

    if (navbar) {
      navbar.style.removeProperty("display");
      navbar.style.removeProperty("height");
    }
    document.body.classList.remove("login-screen");
    document.body.style.removeProperty("padding-top");
  }, [isAuthRoute, location.pathname]);



  // Obtener las Business Units del backend
  useEffect(() => {
    if (isPublicBooking || isCommercialRoute || isPublicPosDeviceRoute || isAuthRoute || isCorporatePurchasingRoute) return;
    const token = readStoredAuthToken();
    if (!token) return;

    axios.get("/api/business-units")
      .then(response => {
        setBusinessUnits(response.data);
      })
      .catch(error => {
        console.error(error);
      });
  }, [isAuthRoute, isCommercialRoute, isCorporatePurchasingRoute, isPublicBooking, isPublicPosDeviceRoute, location.pathname]);

  // Keep idle sessions revocable. The backend also checks every authenticated
  // API request, while this heartbeat guarantees a deactivated user is sent
  // back to login even if the current screen is otherwise idle.
  useEffect(() => {
    if (
      demoModeActive ||
      normalizedPathname === "/" ||
      normalizedPathname === "/login" ||
      (normalizedPathname.startsWith("/reset-password") || normalizedPathname.startsWith("/accept-invitation")) ||
      isPublicBooking ||
      isCommercialRoute ||
      isPublicPosDeviceRoute
    ) {
      return undefined;
    }

    const sessionRefresh = createSessionRefreshController({
      readToken: readStoredAuthToken,
      requestSession: (signal) => axios.get("/api/auth/session", { signal }),
      onResponse: (response) => {
        const refreshedUser = response?.data?.user;
        if (!refreshedUser) return;

        const currentUser = readStoredUser();
        if (
          sessionAccessFingerprint(currentUser) !==
          sessionAccessFingerprint(refreshedUser)
        ) {
          persistAuthSession(response.data, currentUser);
          window.location.reload();
        }
      },
    });

    // These entries validate the session before mounting their content. Keep
    // the heartbeat, but do not duplicate the gate's initial request.
    if (normalizedPathname !== "/select-workspace" && !isCorporatePurchasingRoute) {
      sessionRefresh.refresh();
    }
    const intervalId = window.setInterval(sessionRefresh.refresh, 30_000);
    return () => {
      window.clearInterval(intervalId);
      sessionRefresh.dispose();
    };
  }, [
    demoModeActive,
    normalizedPathname,
    isCorporatePurchasingRoute,
    isPublicBooking,
    isCommercialRoute,
    isPublicPosDeviceRoute,
  ]);


  // Manejar el cambio de selección
  const handleBusinessUnitChange = (event) => {
    const unitId = event.target.value;
    setSelectedBusinessUnit(unitId);
    if (unitId) {
      navigate(`/reservations?business_unit_id=${unitId}`);
    }
  };

  //useDraftCleanupOnNavigation();

  console.log("🧭 Rendering NewReservation route", {
    pathname: location.pathname,
    search: location.search,
  });

  const demoReadOnlyRedirectPath = getDemoReadOnlyRedirectPath({
    pathname: normalizedPathname,
    search: location.search,
    demoModeActive,
    demoBusinessUnitId: DEMO_BUSINESS_UNIT_ID,
  });

  if (demoReadOnlyRedirectPath) {
    return <NavigateToDemoReadOnlyPath to={demoReadOnlyRedirectPath} />;
  }


  return (
    <div
      className={`${
        isPublicBooking
          ? "min-vh-100 text-light"
          : isCommercialRoute
          ? "commercial-app-shell text-light"
          : isPublicPosDeviceRoute
            ? "min-vh-100 text-light"
            : usesPilcoreWorkspaceShell
              ? "pms-app-shell min-vh-100"
              : "container-fluid min-vh-100 text-light"
      }${demoModeActive ? " demoModeApp" : ""}`}
      style={isCommercialRoute || usesPilcoreWorkspaceShell ? undefined : { backgroundColor: "#000" }}
    >
      {/* Muestra la Navbar solo cuando NO estamos en la ruta "/" */}
      {!isPublicBooking &&
        !isCommercialRoute &&
        !isGuestFeedbackSurveyRoute &&
        !isAuthRoute &&
        !location.pathname.startsWith("/admin") &&
        !location.pathname.startsWith("/purchasing") &&
        !isPosRoute &&
        !isAnalyticsRoute &&
        !isCorporatePurchasingRoute &&
        !isOnboardingRoute &&
        !isCrmRoute &&
        !isPmsPanelRoute && (
        <Navbar />
      )}

  
      {/* Agregamos un margen superior para evitar que la navbar tape contenido */}
      <div
        style={{
          marginTop:
            isAuthRoute ||
            isPublicBooking ||
            isCommercialRoute ||
            isGuestFeedbackSurveyRoute ||
            location.pathname.startsWith("/admin") ||
            location.pathname.startsWith("/purchasing") ||
            isPosRoute ||
            isAnalyticsRoute ||
            isCorporatePurchasingRoute ||
            isOnboardingRoute ||
            isCrmRoute ||
            isPmsPanelRoute
              ? 0
              : "70px",
        }}
      >
        <Routes>
          {/* 🌐 Public Booking Engine */}
          <Route path="/booking/:slug/cancel" element={<BookingCancel />} />
          <Route path="/guest-feedback-survey/:token" element={<GuestFeedbackSurveyPublicPage />} />

          {/* Mismo componente, 2 paths */}
          <Route path="/booking/:slug" element={<BookingApp />} />
          <Route path="/booking/:slug/draft/:groupId" element={<BookingApp />} />
          <Route path="/comercial" element={<CommercialSite />} />



          
          <Route path="/" element={demoModeActive ? <DemoEntry /> : <LoginScreen />} />
          <Route path="/demo" element={<DemoEntry />} />
          <Route path="/new-reservation" element={<NewReservation />} />
          <Route path="/new-reservation/:id" element={<NewReservation />} />
          {/* ✅ NUEVA PANTALLA ESTABLE DE GRUPO */}
          <Route path="/select-customer" element={<SelectCustomer />} />
          <Route path="/new-customer" element={<NewCustomer />} />
          <Route path="/reservation/:id" element={<ReservationDetails />} />
          <Route path="/check-in/:id" element={<CheckIn />} />
          <Route path="/edit-reservation/:id" element={<EditReservation />} />
          <Route path="/new-stop-sell" element={<NewStopSell />} />
          <Route path="/new-room-rate" element={<RoomRateForm />} />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="/login/*" element={<Navigate to="/" replace />} />
          <Route path="/reset-password" element={<ResetPasswordScreen />} />
          <Route path="/accept-invitation" element={<AcceptInvitationScreen />} />
          <Route path="/select-module" element={<ModuleSelector />} />
          <Route path="/select-workspace" element={<SessionAccessGate key="workspace"><WorkspaceSelectionScreen /></SessionAccessGate>} />
          <Route
            path="/settings/users"
            element={<PrivateRoute><GlobalUsersRoles /></PrivateRoute>}
          />

          <Route element={<PmsPanel />}>
            <Route
              path="reservation-group/:reservation_group_id"
              element={
                <PrivateRoute>
                  <ReservationGroupView />
                </PrivateRoute>
              }
            />
            <Route
              path="reservation-group/:reservation_group_id/account"
              element={
                <PrivateRoute>
                  <ReservationAccountView />
                </PrivateRoute>
              }
            />
            <Route
              path="reservations"
              element={
                <PrivateRoute>
                  {pmsEntryPath ? (
                    <Navigate to={`${pmsEntryPath}${location.search || ""}`} replace />
                  ) : (
                    <PmsPermissionRoute
                      allowed={false}
                      message="You don't have an authorized PMS entry page."
                    />
                  )}
                </PrivateRoute>
              }
            />
            <Route
              path="reservations/dashboard"
              element={
                <PrivateRoute>
                  <PmsDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="reservations/list"
              element={
                <PrivateRoute>
                  <ReservationsTabs forcedTab="reservations" />
                </PrivateRoute>
              }
            />
            <Route
              path="reservations/waitlist"
              element={
                demoModeActive ? (
                  <NavigatePreservingSearch to="/reservations/list" />
                ) : (
                  <PrivateRoute>
                    <ReservationsTabs forcedTab="waitlist" />
                  </PrivateRoute>
                )
              }
            />
            <Route
              path="reservations/availability"
              element={
                <PrivateRoute>
                  <ReservationsTabs forcedTab="availability" />
                </PrivateRoute>
              }
            />
            <Route
              path="reservations/allocations"
              element={
                <PrivateRoute>
                  <ReservationsTabs forcedTab="allocations" />
                </PrivateRoute>
              }
            />

            <Route
              path="reports"
              element={
                <PrivateRoute>
                  <Navigate to={`/reports/monthly-stay-performance${location.search || ""}`} replace />
                </PrivateRoute>
              }
            />
            <Route
              path="reports/monthly-stay-performance"
              element={
                <PrivateRoute>
                  <PmsPermissionRoute
                    allowed={canAccessReports}
                    message="You don't have permission to access Reports."
                  >
                    <ReportsStayPerformance />
                  </PmsPermissionRoute>
                </PrivateRoute>
              }
            />

            <Route path="front-desk" element={<FrontDesk forcedTab="check-in" />} />
            <Route path="front-desk/check-ins" element={<FrontDesk forcedTab="check-in" />} />
            <Route path="front-desk/in-house" element={<FrontDesk forcedTab="in-house" />} />
            <Route path="front-desk/check-outs" element={<FrontDesk forcedTab="check-out" />} />

            <Route path="rooms-management" element={<RoomsManagement forcedTab="matrix" />} />
            <Route path="rooms-management/matrix" element={<RoomsManagement forcedTab="matrix" />} />
            <Route
              path="rooms-management/assignment"
              element={<NavigateToRoomNumbersGrid />}
            />
            <Route path="rooms-management/blocks" element={<RoomsManagement forcedTab="blocks" />} />

            <Route path="settings" element={<Settings key="settings-reservation-root" forcedTab="reservation" />} />
            <Route path="settings/reservation" element={<Settings key="settings-reservation" forcedTab="reservation" />} />
            <Route path="settings/front-desk" element={<Settings key="settings-front-desk" forcedTab="front_desk" />} />
            <Route path="settings/revenue" element={<Settings key="settings-revenue" forcedTab="revenue" />} />
            <Route path="settings/currencies" element={<Settings key="settings-currencies" forcedTab="currencies" />} />
            <Route path="settings/extras" element={<Settings key="settings-extras" forcedTab="extras" />} />
            <Route path="settings/charges" element={<Settings key="settings-charges" forcedTab="charges" />} />
            <Route
              path="settings/legal-entity"
              element={
                <PrivateRoute>
                  <PmsPermissionRoute
                    allowed={canViewLegalEntitySettings}
                    message="You don't have permission to view hotel and legal entity details."
                  >
                    <LegalEntitySettings />
                  </PmsPermissionRoute>
                </PrivateRoute>
              }
            />
            <Route path="settings/appearance" element={<Settings key="settings-appearance" forcedTab="appearance" />} />
            <Route
              path="settings/booking-engine"
              element={<Settings key="settings-booking-engine" forcedTab="booking_engine" />}
            />

            <Route
              path="revenue-management-container"
              element={<RevenueManagementContainer forcedTab="management" />}
            />
            <Route
              path="revenue-management-container/management"
              element={<RevenueManagementContainer forcedTab="management" />}
            />
            <Route
              path="revenue-management-container/matrix"
              element={<RevenueManagementContainer forcedTab="matrix" />}
            />
            <Route
              path="revenue-management-container/market-intelligence"
              element={<MarketIntelligenceView forcedView="rules" />}
            />
            <Route
              path="revenue-management-container/market-intelligence/rules"
              element={<MarketIntelligenceView forcedView="rules" />}
            />
            <Route
              path="revenue-management-container/market-intelligence/competitor-prices"
              element={<MarketIntelligenceView forcedView="competitors" />}
            />
            <Route
              path="revenue-management-container/market-intelligence/internal-vs-market"
              element={<MarketIntelligenceView forcedView="internal-vs-market" />}
            />
            <Route
              path="revenue-management-container/revenue-optimizer"
              element={
                <PrivateRoute>
                  <PmsPermissionRoute
                    allowed={canAccessRevenueOptimizer}
                    message="You don't have permission to access Revenue Optimizer."
                  >
                    <RevenueOptimizerView forcedView="setup" />
                  </PmsPermissionRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="revenue-management-container/revenue-optimizer/setup"
              element={
                <PrivateRoute>
                  <PmsPermissionRoute
                    allowed={canAccessRevenueOptimizer}
                    message="You don't have permission to access Revenue Optimizer."
                  >
                    <RevenueOptimizerView forcedView="setup" />
                  </PmsPermissionRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="revenue-management-container/revenue-optimizer/signals-forecast"
              element={
                <PrivateRoute>
                  <PmsPermissionRoute
                    allowed={canAccessRevenueOptimizer}
                    message="You don't have permission to access Revenue Optimizer."
                  >
                    <RevenueOptimizerView forcedView="signals-forecast" />
                  </PmsPermissionRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="revenue-management-container/revenue-optimizer/market-benchmark"
              element={
                <PrivateRoute>
                  <PmsPermissionRoute
                    allowed={canAccessRevenueOptimizer}
                    message="You don't have permission to access Revenue Optimizer."
                  >
                    <RevenueOptimizerView forcedView="market-benchmark" />
                  </PmsPermissionRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="revenue-management-container/revenue-optimizer/recommendations"
              element={
                <PrivateRoute>
                  <PmsPermissionRoute
                    allowed={canAccessRevenueOptimizer}
                    message="You don't have permission to access Revenue Optimizer."
                  >
                    <RevenueOptimizerView forcedView="recommendations" />
                  </PmsPermissionRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="edit-room-rate/:id"
              element={<EditRatePrice forcedTab="internal" hideViewTabs={true} />}
            />
            <Route
              path="reviews-intelligence/dashboard"
              element={
                <PrivateRoute>
                  <PmsPermissionRoute
                    allowed={canAccessReviewIntelligence}
                    message="You don't have permission to access Review Intelligence."
                  >
                    <ReviewsDashboardPage />
                  </PmsPermissionRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="reviews-intelligence/operations"
              element={
                <PrivateRoute>
                  <PmsPermissionRoute
                    allowed={canAccessReviewIntelligence}
                    message="You don't have permission to access Review Intelligence."
                  >
                    <ReviewOperationsPage />
                  </PmsPermissionRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="reviews-intelligence/trends-actions"
              element={
                <PrivateRoute>
                  <PmsPermissionRoute
                    allowed={canAccessReviewIntelligence}
                    message="You don't have permission to access Review Intelligence."
                  >
                    <TrendsActionsPage />
                  </PmsPermissionRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="reviews-intelligence/all-reviews"
              element={
                <PrivateRoute>
                  <PmsPermissionRoute
                    allowed={canAccessReviewIntelligence}
                    message="You don't have permission to access Review Intelligence."
                  >
                    <AllReviewsPage />
                  </PmsPermissionRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="reviews-intelligence/review-import"
              element={
                <PrivateRoute>
                  <PmsPermissionRoute
                    allowed={canAccessReviewIntelligence}
                    message="You don't have permission to access Review Intelligence."
                  >
                    <ReviewImportPage />
                  </PmsPermissionRoute>
                </PrivateRoute>
              }
            />
            <Route
              path="reviews-intelligence/surveys"
              element={
                <PrivateRoute>
                  <PmsPermissionRoute
                    allowed={canAccessReviewIntelligence}
                    message="You don't have permission to access Review Intelligence."
                  >
                    <GuestFeedbackSurveysPage />
                  </PmsPermissionRoute>
                </PrivateRoute>
              }
            />
          </Route>
          {/* 🧩 Nuevo módulo de compras */}
          <Route path="/purchasing" element={<Navigate to="/purchasing/dashboard" replace />} />
          <Route path="/purchasing/*" element={<PurchasingApp />} />
          <Route path="/pos/guest-order" element={<GuestOrderPage />} />
          <Route path="/pos/guest-order/:token" element={<GuestOrderPage />} />
          <Route path="/pos/table-order/session" element={<GuestOrderPage orderMode="tableQrSession" />} />
          <Route path="/pos/table-order/:tableToken" element={<GuestOrderPage orderMode="tableQr" />} />
          <Route path="/pos/service-display" element={<ServiceDisplayPage />} />
          <Route path="/pos/service-display/:token" element={<ServiceDisplayPage />} />
          <Route path="/pos/customer-credit/:accessToken" element={<CustomerCreditPage />} />
          <Route path="/pos" element={<Navigate to="/pos/sales" replace />} />
          <Route path="/pos/*" element={<PosApp />} />
          <Route path="/analytics" element={<NavigatePreservingSearch to="/analytics/sales" />} />
          <Route path="/analytics/*" element={<AnalyticsApp />} />
          <Route path="/onboarding" element={<Navigate to="/onboarding/language" replace />} />
          <Route path="/onboarding/*" element={<OnboardingApp />} />
          <Route path="/crm" element={<Navigate to="/crm/dashboard" replace />} />
          <Route path="/crm/*" element={<CrmApp />} />
          <Route path="/corporate-purchasing/*" element={<SessionAccessGate key="corporate"><CorporatePurchasingApp /></SessionAccessGate>} />
          <Route path="/admin/purchasing/*" element={<CorporatePurchasingLegacyRedirect />} />

         

          
          <Route path="/admin/*" element={<AdminPanel />}>
            <Route path="business-units" element={<BusinessUnitsList />} />
            <Route path="currencies" element={<CurrenciesAdmin />} />
            <Route path="room-categories" element={<RoomCategoriesList />} />
            <Route path="rooms-list" element={<RoomsList />} />
            <Route path="measurement-units" element={
              <PmsPermissionRoute allowed={isCurrentUserAdmin} message="Solo el administrador principal puede gestionar el catálogo de unidades.">
                <PurchasingMeasurementUnits masterCatalog />
              </PmsPermissionRoute>
            } />
            <Route path="view-db" element={ <ViewDB /> } />
            <Route path="cron" element={<AdminCron /> }/> 
            <Route path="reservation-generator" element={<ReservationGeneratorAdmin />} />
            <Route path="reviews-intelligence/review-sources" element={<ReviewSourcesAdmin />} />
            <Route
              path="reviews-intelligence/review-topics-catalog"
              element={<ReviewTopicsCatalogAdmin />}
            />
            <Route path="market-intelligence" element={<MarketIntelligence />} />
            <Route path="integrations" element={<Integrations />}>
              <Route path="channel-managers" element={<ChannelManagers />} />
              <Route path="payment-gateways" element={<PaymentGateways />} />
              <Route path="crm-providers" element={<CrmProviderIntegrations />} />
              <Route path="pos-purchasing" element={
                <PmsPermissionRoute allowed={isCurrentUserAdmin} message="Solo el administrador principal puede configurar las integraciones POS y Compras.">
                  <PosPurchasingIntegrations />
                </PmsPermissionRoute>
              } />
              <Route path="business-unit-providers" element={
                <PmsPermissionRoute allowed={isCurrentUserAdmin} message="Solo el administrador principal puede configurar los servicios por BU.">
                  <BusinessUnitProviders />
                </PmsPermissionRoute>
              } />
              <Route path="whatsapp-crm" element={<WhatsAppCrmIntegrations />} />
              <Route path="whatsapp-delivery-notes" element={<WhatsAppDeliveryNoteIntegrations />} />
              <Route path="logs" element={<Navigate to="/admin/security/app-logs" replace />} />
            </Route>

            <Route path="security" element={<Navigate to="/admin/security/logs" replace />} />
            <Route path="security/app-logs" element={<AdminLogs />} />
            <Route path="security/logs" element={<AdminSecurityLogs />} />
            <Route path="user-management" element={<UserManagement />} />
            <Route path="onboarding" element={<OnboardingAdminHub />}>
              <Route path="setup" element={<OnboardingSetupAdmin />} />
              <Route path="room-categories" element={<OnboardingRoomCategoriesAdmin />} />
              <Route path="rooms" element={<OnboardingRoomsAdmin />} />
              <Route path="room-rates" element={<OnboardingRoomRatesAdmin />} />
              <Route path="reservation-settings" element={<OnboardingReservationSettingsAdmin />} />
              <Route path="extras-discounts" element={<OnboardingExtrasDiscountsAdmin />} />
              <Route path="booking-engine" element={<OnboardingBookingEngineAdmin />} />
            </Route>
            <Route path="onboarding-setup" element={<Navigate to="/admin/onboarding/setup" replace />} />
            <Route
              path="onboarding-room-categories"
              element={<Navigate to="/admin/onboarding/room-categories" replace />}
            />
            <Route path="onboarding-rooms" element={<Navigate to="/admin/onboarding/rooms" replace />} />
            <Route path="onboarding-room-rates" element={<Navigate to="/admin/onboarding/room-rates" replace />} />
            <Route
              path="onboarding-reservation-settings"
              element={<Navigate to="/admin/onboarding/reservation-settings" replace />}
            />
            <Route
              path="onboarding-extras-discounts"
              element={<Navigate to="/admin/onboarding/extras-discounts" replace />}
            />
            <Route path="onboarding-booking-engine" element={<Navigate to="/admin/onboarding/booking-engine" replace />} />
            <Route path="occupancy-audit" element={<AdminOccupancyAudit />} />
            <Route
              path="inventory-location-reconciliation"
              element={<InventoryLocationReconciliationAdmin />}
            />
            <Route
              path="inventory-location-incidents"
              element={<PmsPermissionRoute allowed={isCurrentUserAdmin} message="Solo el administrador principal puede consultar las incidencias de stock."><InventoryLocationIncidentsAdmin /></PmsPermissionRoute>}
            />

            {/* en el futuro: <Route path="rms" element={<RMSIntegrations />} /> */}
          </Route>


         
        </Routes>
      </div>

      <CrmTaskReminderPopup
        disabled={
          isPublicBooking ||
          isCommercialRoute ||
          isCorporatePurchasingRoute ||
          isGuestFeedbackSurveyRoute ||
          isPublicPosDeviceRoute ||
          normalizedPathname === "/" ||
          normalizedPathname === "/login" ||
          normalizedPathname === "/select-workspace" ||
          (normalizedPathname.startsWith("/reset-password") || normalizedPathname.startsWith("/accept-invitation")) ||
          isOnboardingRoute ||
          !canAccessCrmModule ||
          !readStoredAuthToken()
        }
      />

      {demoModeActive && normalizedPathname !== "/demo" ? <DemoModeBanner /> : null}
      {demoModeActive ? <DemoDesktopOnlyGuard /> : null}
  
      
    </div>
  );
  
}

export default App;
